import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Loader2, ArrowRight, ArrowLeft, Lock, Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { OtpBoxes } from "@/components/OtpBoxes";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = enter email, 2 = code + new pw
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => timerRef.current && clearInterval(timerRef.current), []);

  const startTimer = () => {
    setResendIn(30);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => { if (s <= 1) { clearInterval(timerRef.current); return 0; } return s - 1; });
    }, 1000);
  };

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return toast.error("Enter your email address");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password/send-code", { email: email.trim().toLowerCase() });
      toast.success(data.message || "If your email is registered, a code is on its way.");
      setStep(2);
      startTimer();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset code. Please try again.");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resendIn > 0 || loading) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/send-code", { email: email.trim().toLowerCase() });
      toast.success("Code resent.");
      setCode("");
      startTimer();
    } catch { /* ignored — silent retry */ } finally { setLoading(false); }
  };

  const verify = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code sent to your email");
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/verify", {
        email: email.trim().toLowerCase(),
        code, new_password: pw,
      });
      toast.success("Password reset! Please log in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password. Please request a new code.");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      eyebrow="Reset password"
      title="Forgot your password?"
      subtitle="No worries — we'll email you a 6-digit code so you can pick a new one."
    >
      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.form
            key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            onSubmit={sendCode} className="space-y-5" data-testid="forgot-step1-form"
          >
            <div>
              <Label htmlFor="fp-email" className="text-brand-navy font-semibold">Registered email</Label>
              <div className="mt-2 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
                <Input
                  id="fp-email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@gmail.com"
                  className="pl-11 h-12 rounded-xl border-brand-navy/15"
                  data-testid="forgot-email-input"
                />
              </div>
              <p className="mt-1.5 text-xs text-brand-navy/50">We&#39;ll send a code to this email. Check your spam folder if it doesn&#39;t arrive within a minute.</p>
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="forgot-send-btn"
              className="w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold btn-scale"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset code <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </motion.form>
        ) : (
          <motion.div
            key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="space-y-5" data-testid="forgot-step2-form"
          >
            <button
              type="button"
              onClick={() => { setStep(1); setCode(""); setPw(""); setConfirm(""); }}
              className="inline-flex items-center gap-1 text-brand-navy/60 hover:text-brand-red text-sm font-semibold"
              data-testid="forgot-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Use a different email
            </button>

            <div className="text-center">
              <KeyRound className="w-10 h-10 text-brand-red mx-auto" />
              <div className="mt-3 font-heading font-bold text-brand-navy text-xl">Enter the 6-digit code</div>
              <p className="text-sm text-brand-navy/60 mt-1">
                Sent to <span className="font-semibold text-brand-navy">{email}</span>. It expires in 15 minutes.
              </p>
            </div>

            <OtpBoxes value={code} onChange={setCode} testIdPrefix="forgot-otp" />

            <div>
              <Label className="text-brand-navy font-semibold">New password</Label>
              <div className="mt-2 relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
                <Input
                  type={showPw ? "text" : "password"} required
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  placeholder="Min 8 characters"
                  className="pl-11 pr-12 h-12 rounded-xl border-brand-navy/15"
                  data-testid="forgot-new-input"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/50 hover:text-brand-red p-1" aria-label="toggle" data-testid="forgot-new-toggle">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-brand-navy font-semibold">Confirm new password</Label>
              <div className="mt-2 relative">
                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
                <Input
                  type={showPw ? "text" : "password"} required
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter new password"
                  className={`pl-11 h-12 rounded-xl border-brand-navy/15 ${confirm && confirm !== pw ? "border-red-500" : ""}`}
                  data-testid="forgot-confirm-input"
                />
              </div>
              {confirm && confirm !== pw && (
                <p className="mt-1.5 text-xs text-red-600 font-semibold">Passwords do not match</p>
              )}
            </div>

            <Button
              onClick={verify} disabled={loading || code.length !== 6 || pw.length < 8 || pw !== confirm}
              data-testid="forgot-verify-btn"
              className="w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold btn-scale disabled:opacity-40"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Reset password <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>

            <div className="text-center text-sm text-brand-navy/60">
              Didn&#39;t receive it?{" "}
              <button
                type="button" onClick={resend} disabled={resendIn > 0 || loading}
                data-testid="forgot-resend-btn"
                className="text-brand-red font-semibold hover:underline disabled:opacity-40 disabled:no-underline"
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-8 text-center text-sm text-brand-navy/60">
        Remembered it?{" "}
        <Link to="/login" data-testid="forgot-goto-login" className="text-brand-red font-semibold hover:underline">
          Back to log in
        </Link>
      </p>
    </AuthShell>
  );
}
